/**
 * RL-1186: same-day medicine delivery at the dispensary (API RL-985,
 * `src/modules/patient-medicines`). Shapes follow GET /dispensary/deliveries
 * (`staffDeliveryView`) and POST /dispensary/deliveries/{id}/status
 * (`DeliveryStatusDto`).
 */

export type DeliveryStatus =
  | 'requested'
  | 'packed'
  | 'out_for_delivery'
  | 'delivered'
  | 'failed'
  | 'returned'
  | 'cancelled';

/** `status` query of GET /dispensary/deliveries (default `open`). */
export type DeliveryQueueFilter = 'open' | 'all' | DeliveryStatus;

/** Staff courier steps (`DESK_STATUSES`); packing is "Dispensed" with a delivery. */
export type DeskStatus =
  'out_for_delivery' | 'delivered' | 'failed' | 'returned' | 'cancelled';

export interface StaffDelivery {
  id: string;
  prescriptionId: string;
  prescriptionNumber: string | null;
  status: DeliveryStatus;
  medicines: string[];
  address: string;
  deliverBy: string;
  branch: { id: string; name: string; phone: string | null };
  fee: {
    /** Sen; null for a clinic delivery without a fee. */
    feeSen: number | null;
    billId: string | null;
    payAt: 'counter';
    inAppPaymentAvailable: boolean;
  };
  steps: {
    key: 'packed' | 'with_courier' | 'delivered';
    state: 'done' | 'current' | 'upcoming';
    at: string | null;
  }[];
  courier: {
    name: string | null;
    phone: string | null;
    trackingUrl: string | null;
  } | null;
  proof: {
    deliveredAt: string | null;
    recipientName: string | null;
    idChecked: boolean | null;
    note: string | null;
  } | null;
  failedAt: string | null;
  returnedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: 'patient' | 'clinic' | null;
  canCancel: boolean;
  createdAt: string;
  /** Send it back with every courier step (409 VERSION_CONFLICT when stale). */
  version: number;
  /** `app` = the patient asked in the app; `clinic` = packed without a request. */
  source: 'app' | 'clinic' | string;
  patient: { id: string; fullName: string; phone: string | null };
  provider: string | null;
  providerRef: string | null;
  failureReason: string | null;
  cancelReason: string | null;
  /** Cancelled or returned but the fee bill was paid: refund it in Billing. */
  refundDue: boolean;
}

/** POST /dispensary/deliveries/{id}/status body (`DeliveryStatusDto`). */
export interface DeliveryStatusInput {
  status: DeskStatus;
  version: number;
  courierName?: string;
  courierPhone?: string;
  trackingUrl?: string;
  recipientName?: string;
  idChecked?: boolean;
  note?: string;
  reason?: string;
}

/** GET /dispensary/delivery-settings. Times are minutes after midnight (branch time). */
export interface DeliverySettings {
  /** Sen; null = same-day delivery is switched off. */
  feeSen: number | null;
  branchIds: string[];
  cutoffMin: number;
  deliverByMin: number;
  branches: { id: string; name: string }[];
}

export const DELIVERY_PERMISSIONS = {
  read: 'dispensary:read',
  /** Packing ("Dispensed" with a delivery) and every courier step. */
  manage: 'dispensary:dispense',
} as const;
